import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Divider,
  Typography,
  IconButton,
  InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { useNavigate } from "react-router-dom";

import { signUpEmailPassword, loginEmailPassword } from "../lib/emailPasswordAuth";
import { sanitizeEmail, sanitizePassword } from "../lib/sanitize";

export default function AuthModal({ open, mode, onClose, onStartWallet, onSwitchMode  }) {
  const nav = useNavigate();

  // step: "email" -> "password"
  const [step, setStep] = useState("email");

  const [emailRaw, setEmailRaw] = useState("");
  const [passRaw, setPassRaw] = useState("");
  const [pass2Raw, setPass2Raw] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  const title = useMemo(
    () => (mode === "signup" ? "Welcome to PromosPay" : "Welcome back"),
    [mode]
  );

useEffect(() => {
  // при смене режима сбрасываем на email step и чистим ошибки
  setStep("email");
  setErr("");
  setPassRaw("");
  setPass2Raw("");
  setShowPass(false);
  setShowPass2(false);
}, [mode]);


  const modalPaperSx = {
    "& .MuiDialog-paper": {
      width: "min(520px, 92vw)",
      borderRadius: 3,
      background:
        "linear-gradient(180deg, rgba(18,22,28,.96) 0%, rgba(10,13,17,.92) 100%)",
      border: "1px solid rgba(255,255,255,.10)",
      boxShadow: "0 18px 60px rgba(2,8,23,.65)",
      color: "#e5e7eb",
      overflow: "hidden",
      backdropFilter: "blur(10px)"
    }
  };

  const inputSx = {
    "& .MuiInputBase-root": {
      borderRadius: 2,
      bgcolor: "rgba(255,255,255,.06)",
      color: "#e5e7eb",
      border: "1px solid rgba(255,255,255,.10)"
    },
    "& input": { color: "#e5e7eb" },
    "& .MuiInputBase-root:hover": {
      borderColor: "rgba(255,255,255,.18)"
    },
    "& .MuiFormLabel-root": { color: "rgba(229,231,235,.75)" }
  };

  const primaryBtnSx = {
    borderRadius: 2,
    fontWeight: 900,
    textTransform: "none",
    height: 44,
    background: "linear-gradient(90deg, rgba(20,241,149,.85), rgba(56,189,248,.60))",
    color: "#071018",
    "&:hover": {
      background: "linear-gradient(90deg, rgba(20,241,149,.95), rgba(56,189,248,.72))"
    },
    "&.Mui-disabled": { opacity: 0.6 }
  };

  const ghostBtnSx = {
    borderRadius: 2,
    fontWeight: 900,
    textTransform: "none",
    height: 44,
    bgcolor: "rgba(255,255,255,.10)",
    color: "#e5e7eb",
    border: "1px solid rgba(255,255,255,.10)",
    "&:hover": { bgcolor: "rgba(255,255,255,.14)" }
  };

  const reset = () => {
    setStep("email");
    setEmailRaw("");
    setPassRaw("");
    setPass2Raw("");
    setErr("");
    setShowPass(false);
    setShowPass2(false);
    setBusy(false);
  };

  const safeEmail = sanitizeEmail(emailRaw);
  const safePass = sanitizePassword(passRaw);
  const safePass2 = sanitizePassword(pass2Raw);

  const emailLooksOk = safeEmail.includes("@") && safeEmail.length <= 254;

  const goEmailStep = () => {
    setErr("");
    setStep("email");
    setPassRaw("");
    setPass2Raw("");
  };

  const onContinueFromEmail = () => {
    setErr("");
    if (!emailLooksOk) {
      setErr("Enter a valid email.");
      return;
    }
    setStep("password");
  };

  const onSubmitPassword = async () => {
    try {
      setErr("");

      if (!emailLooksOk) {
        setErr("Enter a valid email.");
        setStep("email");
        return;
      }

      // базовая защита: не даём гигантские строки и мусор — sanitizePassword уже ограничивает до 512
      if (!safePass || safePass.length < 6) {
        setErr("Password must be at least 6 characters.");
        return;
      }

      if (mode === "signup" && safePass !== safePass2) {
        setErr("Passwords do not match.");
        return;
      }

      setBusy(true);

      if (mode === "signup") {
        const user = await signUpEmailPassword(safeEmail, safePass);
        onClose?.();
        reset();
        nav("/verify-email", { replace: true, state: { email: user.email, next: "/market" } });
        return;
      }

      const user = await loginEmailPassword(safeEmail, safePass);

      await user.reload();
      if (!user.emailVerified) {
        onClose?.();
        reset();
        nav("/verify-email", { replace: true, state: { email: user.email, next: "/market" } });
        return;
      }

      onClose?.();
      reset();
      nav("/market", { replace: true });
    } catch (e) {
      setErr(e?.message || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  const onWalletClick = () => {
    onClose?.();
    reset();
    setTimeout(() => onStartWallet?.(), 0);
  };

  const onDialogClose = () => {
    onClose?.();
    reset();
  };

  return (
    <Dialog open={open} onClose={onDialogClose} sx={modalPaperSx}>
      <DialogTitle sx={{ fontWeight: 900, textAlign: "center", pt: 2.5 }}>
        {title}
        <IconButton
          onClick={onDialogClose}
          sx={{ position: "absolute", right: 10, top: 10, color: "rgba(229,231,235,.9)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ mt: 2, textAlign: "center" }}>
  {mode === "login" ? (
    <Typography sx={{ fontSize: 12.5, opacity: 0.8 }}>
      Don’t have an account?{" "}
      <Box
        component="span"
        onClick={() => onSwitchMode?.("signup")}
        sx={{
          cursor: "pointer",
          fontWeight: 900,
          color: "#38bdf8",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Sign Up
      </Box>
    </Typography>
  ) : (
    <Typography sx={{ fontSize: 12.5, opacity: 0.8 }}>
      Already have an account?{" "}
      <Box
        component="span"
        onClick={() => onSwitchMode?.("login")}
        sx={{
          cursor: "pointer",
          fontWeight: 900,
          color: "#38bdf8",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Login
      </Box>
    </Typography>
  )}
</Box>


      <DialogContent sx={{ pb: 3 }}>
        {/* STEP 1: EMAIL */}
        {step === "email" && (
          <>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <TextField
                fullWidth
                placeholder="Email address"
                value={emailRaw}
                onChange={(e) => setEmailRaw(e.target.value)}
                size="small"
                autoComplete="email"
                inputProps={{ maxLength: 260 }}
                sx={inputSx}
              />
              <Button
                onClick={onContinueFromEmail}
                disabled={busy || !emailLooksOk}
                sx={{
                  minWidth: 120,
                  ...ghostBtnSx,
                  "&.Mui-disabled": { opacity: 0.45, color: "#9ca3af" }
                }}
              >
                Continue
              </Button>
            </Box>

            {err && (
              <Typography sx={{ mt: 1.25, color: "#fca5a5", fontSize: 13 }}>
                {err}
              </Typography>
            )}

            {/* OR */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2, opacity: 0.7 }}>
              <Divider sx={{ flex: 1, borderColor: "rgba(255,255,255,.12)" }} />
              <Typography variant="caption" sx={{ letterSpacing: 1.5 }}>
                OR
              </Typography>
              <Divider sx={{ flex: 1, borderColor: "rgba(255,255,255,.12)" }} />
            </Box>

            <Button fullWidth onClick={onWalletClick} sx={primaryBtnSx} disabled={busy}>
              Your crypto wallet
            </Button>

            <Typography sx={{ mt: 1.25, opacity: 0.75, fontSize: 12, lineHeight: 1.35 }}>
              We’ll ask for a password after you confirm your email address.
            </Typography>
          </>
        )}

        {/* STEP 2: PASSWORD(S) */}
        {step === "password" && (
          <>
            <Typography sx={{ opacity: 0.85, fontSize: 13 }}>
              Email: <b>{safeEmail}</b>
            </Typography>

            <Box sx={{ display: "grid", gap: 1.25, mt: 1.25 }}>
              <TextField
                fullWidth
                placeholder="Password"
                type={showPass ? "text" : "password"}
                value={passRaw}
                onChange={(e) => setPassRaw(e.target.value)}
                size="small"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                inputProps={{ maxLength: 520 }}
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPass((v) => !v)}
                        edge="end"
                        sx={{ color: "rgba(229,231,235,.8)" }}
                      >
                        {showPass ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {mode === "signup" && (
                <TextField
                  fullWidth
                  placeholder="Repeat password"
                  type={showPass2 ? "text" : "password"}
                  value={pass2Raw}
                  onChange={(e) => setPass2Raw(e.target.value)}
                  size="small"
                  autoComplete="new-password"
                  inputProps={{ maxLength: 520 }}
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPass2((v) => !v)}
                          edge="end"
                          sx={{ color: "rgba(229,231,235,.8)" }}
                        >
                          {showPass2 ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}

              {err && (
                <Typography sx={{ color: "#fca5a5", fontSize: 13 }}>
                  {err}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  onClick={goEmailStep}
                  disabled={busy}
                  sx={{ ...ghostBtnSx, flex: 1 }}
                >
                  Back
                </Button>

                <Button
                  onClick={onSubmitPassword}
                  disabled={busy || !safePass || safePass.length < 6 || (mode === "signup" && safePass !== safePass2)}
                  sx={{ ...primaryBtnSx, flex: 1 }}
                >
                  {busy ? "..." : (mode === "signup" ? "Create account" : "Login")}
                </Button>
              </Box>

              <Typography sx={{ opacity: 0.7, fontSize: 12, lineHeight: 1.35 }}>
                Tip: if you verify email on your phone, come back on this device and click “I verified — Refresh”.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

